import React, { useState, useEffect } from "react";
import {
  RefreshCw,
  Users,
  AlertTriangle,
  UserX
} from "lucide-react";
import {
  getAbsentStudentsToday
} from "../../api/studentattendance/studentAttendancePortalApi";
import {
  Card,
  CardContent,
  Badge,
  Button,
  IconButton,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const AbsentDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAbsentStudentsToday();
      setData(res);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load absent students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const groups = data?.groupedBySection || [];
  const totalAbsent = data?.totalAbsent ?? 0;
  const date = data?.date || "";

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-neutral-200 dark:border-neutral-800/80">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Absent Students Today
          </h1>
          {date && (
            <p className="text-xs sm:text-sm font-semibold text-neutral-500 dark:text-neutral-400 mt-1">
              Date: {date}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Badge variant="danger" className="px-3 py-1 text-xs font-bold rounded-full shadow-2xs">
            {totalAbsent} Absent
          </Badge>
          <IconButton
            onClick={load}
            variant="outline"
            size="sm"
            title="Refresh"
            className="rounded-xl border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800 shadow-2xs"
          >
            <RefreshCw size={16} />
          </IconButton>
        </div>
      </div>

      

      {loading ? (
        <div className="space-y-4">
          <Skeleton height={120} width="100%" className="rounded-2xl" />
          <Skeleton height={120} width="100%" className="rounded-2xl" />
        </div>
      ) : totalAbsent === 0 ? (
        <Card className="border border-emerald-200/80 bg-emerald-50/50 rounded-2xl p-8 sm:p-12 text-center shadow-xs">
          <CardContent className="p-0 flex flex-col items-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 mb-3 shadow-2xs">
              <Users size={32} />
            </div>
            <p className="text-base sm:text-lg font-bold text-emerald-900">
              All students are present today 🎉
            </p>
            <p className="text-xs font-semibold text-emerald-700 mt-1">
              No absences recorded across all assigned sections.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g, idx) => {
            const studentList = g.students || [];
            return (
              <Card key={idx} className="border border-neutral-200 dark:border-neutral-800/80 bg-white dark:bg-neutral-900 rounded-2xl shadow-xs overflow-hidden">
                <div className="flex items-center justify-between gap-3 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/60 px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-rose-100 text-rose-700 ring-1 ring-rose-500/20 shadow-2xs">
                      <UserX size={16} />
                    </span>
                    <h2 className="text-sm font-extrabold text-neutral-800 dark:text-neutral-100">
                      Class {g.className || "—"} – Section {g.sectionName || "—"}
                    </h2>
                  </div>
                  <Badge variant="danger" className="px-2.5 py-0.5 text-[11px] font-extrabold rounded-full">
                    {studentList.length} Absent
                  </Badge>
                </div>

                <CardContent className="p-4 sm:p-5">
                  <ul className="divide-y divide-slate-100 dark:divide-neutral-800">
                    {studentList.map((s, i) => {
                      const name =
                        s.name ||
                        `${s.firstName || ""} ${s.lastName || ""}`.trim() ||
                        "—";
                      return (
                        <li
                          key={s._id || s.studentId || i}
                          className="py-3 first:pt-0 last:pb-0 flex flex-wrap items-center justify-between gap-2 text-xs"
                        >
                          <div className="flex items-center gap-3 min-w-[160px]">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 dark:bg-neutral-900 text-neutral-700 dark:text-neutral-200 font-bold text-[11px]">
                              {(name[0] || "?").toUpperCase()}
                            </div>
                            <span className="font-extrabold text-neutral-800 dark:text-neutral-100">
                              {name}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 text-neutral-500 dark:text-neutral-400 font-semibold">
                            <span>Adm: {s.admissionNo || "—"}</span>
                            {s.rollNumber && (
                              <span>· Roll: {s.rollNumber}</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AbsentDashboard;